import { useState, useMemo } from 'react';
import { ArrowUpload24Regular, Delete16Regular } from '@fluentui/react-icons';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/Dialog';
import { Upload } from '@/components/ui/Upload';
import { toast } from 'sonner';
import { Alert } from '@/components/ui/Alert';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import PatternPreview from './PatternPreview';
import {
  useGetSolutionPatterns,
  useUploadSolutionPattern,
  LoadedSolutionPattern,
} from '../../../hooks/api';
import {
  UploadedPattern,
  PatternSearchDialogProps,
  SOLID_PATTERN,
  isSolidPattern,
} from '../../../types';

const PatternSearchDialog = ({
  open,
  onClose,
  onSelect,
  existingPatterns = [],
}: PatternSearchDialogProps) => {
  const [uploadedPatterns, setUploadedPatterns] = useState<UploadedPattern[]>(existingPatterns);
  const [selectingPattern, setSelectingPattern] = useState<string | null>(null);

  const {
    data: paginatedData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetSolutionPatterns();

  const { mutate: uploadPattern, isLoading: isUploading } = useUploadSolutionPattern();

  // Flatten all pages into a single array
  const solutionPatterns = useMemo(() => {
    if (!paginatedData?.pages) return [];
    return paginatedData.pages.flatMap((page) => page.patterns);
  }, [paginatedData]);

  // Convert LoadedSolutionPattern to UploadedPattern format
  const convertToUploadedPattern = (pattern: LoadedSolutionPattern): UploadedPattern => ({
    id: pattern.id,
    name: pattern.title,
    pattern: pattern.pattern,
    fileContent: pattern.fileContent,
  });

  const handleFileUpload = async (file: File) => {
    // Get title from filename (without extension)
    const title = file.name.replace(/\.pat$/i, '');

    uploadPattern(
      { file, title },
      {
        onSuccess: () => {
          toast.success('Pattern úspěšně nahrán');
        },
        onError: (error) => {
          console.error(error);
        },
      }
    );

    return false; // Prevent default upload behavior
  };

  const handleDeletePattern = (patternId: string) => {
    // Only delete local patterns (those starting with 'local-')
    if (patternId.startsWith('local-')) {
      setUploadedPatterns((prev) => prev.filter((p) => p.id !== patternId));
    }
  };

  const handlePatternClick = async (pattern: UploadedPattern | LoadedSolutionPattern) => {
    const id = pattern.id;
    setSelectingPattern(id);
    try {
      // Small delay to show loading state
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Convert if needed
      const uploadedPattern: UploadedPattern =
        'name' in pattern
          ? (pattern as UploadedPattern)
          : convertToUploadedPattern(pattern as LoadedSolutionPattern);

      onSelect(uploadedPattern);
      onClose();
    } finally {
      setSelectingPattern(null);
    }
  };

  const allPatterns = useMemo(() => {
    // Combine solid pattern + local uploaded patterns + server patterns
    const serverPatterns = solutionPatterns.map(convertToUploadedPattern);
    return [SOLID_PATTERN, ...uploadedPatterns, ...serverPatterns];
  }, [uploadedPatterns, solutionPatterns]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Vzory</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex-1 overflow-y-auto">
          {/* Upload button */}
          <div className="mb-4">
            <Upload
              accept=".pat"
              beforeUpload={handleFileUpload}
              showUploadList={false}
              disabled={isUploading}
            >
              <Button
                variant="outline"
                className="w-full"
                disabled={isUploading}
                data-testid="pattern-search-upload-btn"
              >
                {isUploading ? (
                  <>
                    <SpinnerOverlay size="sm" />
                    <span>Nahrávání...</span>
                  </>
                ) : (
                  <>
                    <span>Nahrát soubor .pat</span>
                    <ArrowUpload24Regular />
                  </>
                )}
              </Button>
            </Upload>
          </div>

          {/* Error state */}
          {error && (
            <div className="flex justify-center items-center py-8">
              <Alert variant="error" title="Chyba při načítání patternů" />
            </div>
          )}

          {/* Loading state */}
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <SpinnerOverlay size="lg" tip="Načítání patternů..." />
            </div>
          )}

          {/* Patterns grid */}
          {!isLoading && allPatterns.length > 0 && (
            <div className="grid grid-cols-4 gap-3">
              {allPatterns.map((pattern) => {
                const isSelecting = selectingPattern === pattern.id;
                const isLocalPattern = pattern.id.startsWith('local-');
                return (
                  <div
                    key={pattern.id}
                    className={`relative flex flex-col border border-neutral-300 rounded overflow-hidden transition-all ${
                      isSelecting
                        ? 'cursor-wait opacity-50'
                        : 'cursor-pointer hover:border-primary hover:shadow-md'
                    }`}
                    onClick={() => !isSelecting && handlePatternClick(pattern)}
                    data-testid={`pattern-search-item-${pattern.id}`}
                  >
                    {/* Loading overlay */}
                    {isSelecting && (
                      <div className="absolute inset-0 z-20 flex items-center justify-center bg-white bg-opacity-75">
                        <SpinnerOverlay size="sm" />
                      </div>
                    )}

                    {/* Delete button - only for local patterns */}
                    {isLocalPattern && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePattern(pattern.id);
                        }}
                        disabled={isSelecting}
                        className="absolute top-2 right-2 z-10 p-1.5 bg-white rounded-full shadow-xs hover:bg-red-50 transition-colors disabled:opacity-50"
                        data-testid={`pattern-search-delete-btn-${pattern.id}`}
                      >
                        <Delete16Regular className="text-red-500 w-4 h-4" />
                      </button>
                    )}

                    {/* Pattern preview - fills entire box */}
                    <div className="flex aspect-square bg-color4 items-center justify-center overflow-hidden">
                      {isSolidPattern(pattern) ? (
                        <div className="w-full h-full bg-neutral-700" />
                      ) : (
                        <PatternPreview pattern={pattern.pattern} />
                      )}
                    </div>

                    {/* Pattern name */}
                    <div className="p-1 text-xs text-center truncate bg-white border-t">
                      {pattern.name}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Load more button */}
          {hasNextPage && (
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                data-testid="pattern-search-load-more-btn"
              >
                {isFetchingNextPage ? (
                  <>
                    <SpinnerOverlay size="sm" />
                    <span>Načítání...</span>
                  </>
                ) : (
                  'Načíst další'
                )}
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && allPatterns.length === 0 && (
            <div className="text-center text-neutral-500 py-8">
              <p>Nahrajte .pat soubor nebo počkejte na načtení vzorů ze serveru</p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" data-testid="pattern-search-close-btn">
              Zavřít
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PatternSearchDialog;
